<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null || isLoading"
          @click="showCreateForm()"
        >
          New Action Plan
        </b-button>
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field v-if="hasGroupPlants" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" expanded>
                <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
          </b-field>
          <ag-grid-vue
            v-if="actionPlan.length != 0"
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :row-data="actionPlan"
            :grid-options="gridOptions"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
          />
        </div>
      </div>
    </div>
    <b-modal :active.sync="isCreatingActionPlan" scroll="keep" has-modal-card>
      <form v-if="isCreatingActionPlan" @submit.prevent="submitActionPlan">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Create Action Plan
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Deadline">
                <b-datepicker
                  v-model="newActionPlan.deadline"
                  name="newdeadlinedate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                />
              </b-field>
            </b-field>
            <b-field label="PDCA">
              <b-select v-model="newActionPlan.pdca">
                <option value="P">
                  P
                </option>
                <option value="D">
                  D
                </option>
                <option value="C">
                  C
                </option>
                <option value="A">
                  A
                </option>
              </b-select>
            </b-field>
            <b-field label="Problem Description">
              <b-input v-model="newActionPlan.problem_description" type="textarea" />
            </b-field>
            <b-field label="Solution">
              <b-input v-model="newActionPlan.solution" type="textarea" />
            </b-field>
            <b-field label="Source">
              <b-select v-model="newActionPlan.source">
                <option value="Work accident">
                  Work accident
                </option>
                <option value="Risk assessment">
                  Risk assessment
                </option>
                <option value="Near miss">
                  Near miss
                </option>
                <option value="ISO Audit">
                  ISO Audit
                </option>
                <option value="Internal quality audit">
                  Internal quality audit
                </option>
                <option value="Internal safety audit">
                  Internal safety audit
                </option>
                <option value="Customer request">
                  Customer request
                </option>
                <option value="GEO feedback">
                  GEO feedback
                </option>
                <option value="Maintenance">
                  Maintenance
                </option>
                <option value="Improvements">
                  Improvements
                </option>
                <option value="Other">
                  Other
                </option>
              </b-select>
            </b-field>
            <b-field label="Pilot">
              <b-input v-model="newActionPlan.pilot" type="text" />
            </b-field>
            <b-field label="Comment">
              <b-input v-model="newActionPlan.comments" type="textarea" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
    <b-modal :active.sync="isEditingActionPlan" scroll="keep" has-modal-card>
      <form v-if="isEditingActionPlan" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Action Plan
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Deadline">
                <b-datepicker
                  v-model="editableActionPlan.deadline"
                  name="newdeadlinedate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                />
              </b-field>
            </b-field>
            <b-field label="PDCA">
              <b-select v-model="editableActionPlan.pdca">
                <option value="P">
                  P
                </option>
                <option value="D">
                  D
                </option>
                <option value="C">
                  C
                </option>
                <option value="A">
                  A
                </option>
              </b-select>
            </b-field>
            <b-field label="Problem Description">
              <b-input v-model="editableActionPlan.problem_description" type="textarea" />
            </b-field>
            <b-field label="Solution">
              <b-input v-model="editableActionPlan.solution" type="textarea" />
            </b-field>
            <b-field label="Source">
              <b-select v-model="editableActionPlan.source">
                <option value="Work accident">
                  Work accident
                </option>
                <option value="Risk assessment">
                  Risk assessment
                </option>
                <option value="Near miss">
                  Near miss
                </option>
                <option value="ISO Audit">
                  ISO Audit
                </option>
                <option value="Internal quality audit">
                  Internal quality audit
                </option>
                <option value="Internal safety audit">
                  Internal safety audit
                </option>
                <option value="Customer request">
                  Customer request
                </option>
                <option value="GEO feedback">
                  GEO feedback
                </option>
                <option value="Maintenance">
                  Maintenance
                </option>
                <option value="Improvements">
                  Improvements
                </option>
                <option value="Other">
                  Other
                </option>
              </b-select>
            </b-field>
            <b-field label="Pilot">
              <b-input v-model="editableActionPlan.pilot" type="text" />
            </b-field>
            <b-field>
              <b-switch v-model="editableActionPlan.abandonned">
                Abandoned
              </b-switch>
            </b-field>
            <b-field label="Comment">
              <b-input v-model="editableActionPlan.comments" type="textarea" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import { mapState } from 'vuex'
import ActionPlan from '../../services/v3/actionPlan'
import plantSelector from '@/mixins/plantSelector'
import { restoreConsultingPlant } from '@/helpers/plants'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'ActionPlan',
  components: {
  },
  mixins: [plantSelector],
  data: function () {
    return {
      editableActionPlan: null,
      newActionPlan: null,
      selectedPlant: restoreConsultingPlant(),
      isLoading: false,
      storedStatus: '',
      start: null,
      end: new Date(),
      isCreatingActionPlan: false,
      isEditingActionPlan: false,
      actionPlan: [],
      modules: AllModules,
      gridOptions: {
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        columnDefs: [
          { headerName: 'Problem Description',
            field: 'problem_description',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Pilot',
            field: 'pilot',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Source',
            field: 'source',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Solution',
            field: 'solution',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Deadline',
            field: 'deadline',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Days Left',
            field: 'days_left',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'PDCA',
            field: 'pdca',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Comment',
            field: 'comments',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Creation Date',
            field: 'creation_date',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'End Date',
            field: 'done_date',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Resolution Time',
            field: 'close_time',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Abandonned',
            field: 'displayAbandonned',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          }
        ],
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },
  mounted: async function () {
    this.start = new Date()
    this.start.setDate(new Date().getDate() - 30)
    await this.loadPlants()
  },
  methods: {
    resetData () {
      this.actionPlan = []
    },
    async showCreateForm () {
      this.isLoading = true
      this.newActionPlan = {
        id_site: this.selectedPlant.id,
        creation_date: new Date(new Date().toLocaleDateString('en-CA')),
        source: '',
        problem_description: '',
        solution: '',
        pilot: '',
        deadline: new Date(new Date().toLocaleDateString('en-CA')),
        pdca: 'P',
        done_date: null,
        comments: '',
        abandonned: false
      }
      this.isLoading = false
      this.isCreatingActionPlan = true
    },
    async showEditForm (actionPlan) {
      this.isLoading = true
      this.storedStatus = actionPlan.pdca
      this.editableActionPlan = actionPlan
      this.isLoading = false
      this.isEditingActionPlan = true
    },
    getLeftActivationDays (finishDate) {
      var endDate = new Date(finishDate)
      var today = new Date()
      var oneDay = 24 * 60 * 60 * 1000
      return Math.round(Math.abs((today.getTime() - endDate.getTime()) / oneDay))
    },
    getNumberOfDays (start, end) {
      const date1 = new Date(start)
      const date2 = new Date(end)

      // One day in milliseconds
      const oneDay = 1000 * 60 * 60 * 24

      // Calculating the time difference between two dates
      const diffInTime = date2.getTime() - date1.getTime()

      // Calculating the no. of days between two dates
      const diffInDays = Math.round(diffInTime / oneDay)

      return diffInDays
    },
    async getData () {
      if (!this.selectedPlant || !this.selectedPlant.id) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      this.isLoading = true
      try {
        const data = await ActionPlan.getTable({
          siteId: this.selectedPlant.id
        })
        data.forEach(element => {
          this.actionPlan.push({
            id: element.id,
            id_site: element.id_site,
            creation_date: element.creation_date ? new Date(element.creation_date.split('Z')[0]).toLocaleDateString('en-CA') : null,
            source: element.source,
            problem_description: element.problem_description,
            solution: element.solution,
            pilot: element.pilot,
            deadline: element.deadline ? new Date(element.deadline.split('Z')[0]).toLocaleDateString('en-CA') : null,
            days_left: this.getLeftActivationDays(element.deadline),
            pdca: element.pdca,
            done_date: element.done_date ? new Date(element.done_date.split('Z')[0]).toLocaleDateString('en-CA') : null,
            close_time: element.done_date ? this.getNumberOfDays(new Date(element.creation_date.split('Z')[0]).toLocaleDateString('en-CA'), new Date(element.done_date.split('Z')[0]).toLocaleDateString('en-CA')) : null,
            comments: element.comments,
            abandonned: element.abandonned,
            displayAbandonned: element.abandonned ? 'Yes' : 'No'
          })
        })
        if (this.actionPlan.length > 0) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.$buefy.toast.open({ message: 'No corresponding Action Plan found', duration: 3000, type: 'is-danger' })
        }
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
        this.isLoading = false
      }
      this.isLoading = false
    },
    getContextMenuItems (params) {
      let customMenu = []
      const genericMenu = [
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      if (params.node && params.node.data) {
        const selectedActionPlan = {
          id: params.node.data.id,
          id_site: params.node.data.id_site,
          creation_date: params.node.data.creation_date ? new Date(new Date(params.node.data.creation_date).toISOString().split('T')[0] + 'T00:00:00') : null,
          source: params.node.data.source,
          problem_description: params.node.data.problem_description,
          solution: params.node.data.solution,
          pilot: params.node.data.pilot,
          deadline: params.node.data.deadline ? new Date(new Date(params.node.data.deadline).toISOString().split('T')[0] + 'T00:00:00') : null,
          pdca: params.node.data.pdca,
          done_date: params.node.data.done_date ? new Date(new Date(params.node.data.done_date).toISOString().split('T')[0] + 'T00:00:00') : null,
          comments: params.node.data.comments,
          abandonned: params.node.data.abandonned
        }
        customMenu.push({
          name: 'Edit Action Plan',
          action: () => {
            this.showEditForm(selectedActionPlan)
          },
          icon: '<i class="fas fa-fw fa-edit" ></i>'
        })
      }
      const result = [...customMenu, ...genericMenu]
      return result
    },
    submitActionPlan () {
      this.isCreatingActionPlan = false
      try {
        ActionPlan.createActionPlan(
          {
            id_site: this.newActionPlan.id_site,
            creation_date: this.newActionPlan.creation_date ? new Date(this.newActionPlan.creation_date).toLocaleDateString('en-CA') : null,
            source: this.newActionPlan.source,
            problem_description: this.newActionPlan.problem_description,
            solution: this.newActionPlan.solution,
            pilot: this.newActionPlan.pilot,
            deadline: this.newActionPlan.deadline ? new Date(this.newActionPlan.deadline).toLocaleDateString('en-CA') : null,
            pdca: this.newActionPlan.pdca,
            done_date: null,
            comments: this.newActionPlan.comments,
            abandonned: this.newActionPlan.abandonned
          }).then(() => {
          this.$buefy.toast.open({ message: 'Action Plan Created', duration: 3000, type: 'is-success' })
          this.resetData()
          this.getData()
        })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async submitEdit () {
      this.isEditingActionPlan = false
      try {
        await ActionPlan.updateActionPlan({
          id: this.editableActionPlan.id,
          id_site: this.editableActionPlan.id_site,
          creation_date: this.editableActionPlan.creation_date ? new Date(this.editableActionPlan.creation_date).toLocaleDateString('en-CA') : null,
          source: this.editableActionPlan.source,
          problem_description: this.editableActionPlan.problem_description,
          solution: this.editableActionPlan.solution,
          pilot: this.editableActionPlan.pilot,
          deadline: this.editableActionPlan.deadline ? new Date(this.editableActionPlan.deadline).toLocaleDateString('en-CA') : null,
          pdca: this.editableActionPlan.pdca,
          done_date: (this.storedStatus !== this.editableActionPlan.pdca && this.editableActionPlan.pdca === 'A') ? (this.editableActionPlan.done_date ? new Date(this.editableActionPlan.done_date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA')) : (this.editableActionPlan.done_date ? new Date(this.editableActionPlan.done_date).toLocaleDateString('en-CA') : null),
          comments: this.editableActionPlan.comments,
          abandonned: this.editableActionPlan.abandonned
        })
        this.$buefy.toast.open({ message: 'Action Plan Updated', duration: 3000, type: 'is-success' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async deleteActionPlan (actionPlan) {
      try {
        await ActionPlan.archiveActionPlan(actionPlan.id)
        this.$buefy.toast.open({ message: 'Action Plan Deleted', duration: 3000, type: 'is-warning' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>
